/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { useState } from "react";
import { BsFillChatFill } from "react-icons/bs";
import { FaBell, FaEnvelope, FaTag } from "react-icons/fa";
import { GrTest } from "react-icons/gr";
import { HiOutlineDuplicate } from "react-icons/hi";
import { IoMdClose } from "react-icons/io";
import { useRecoilState, useRecoilValue } from "recoil";
import { CampaignActionAtom } from "../../../../atoms/CampaignActionAtom";
import { CampaingBuilderFormAtom } from "../../../../atoms/CampaingBuilderFormAtom";
import { INodeTypes } from "../../../../generated/Api";
import useMediaQuery from "../../../../hook/useMediaQuery";
import { INode } from "../../../../types/CampaignTypes";
import Warning from "../../../Modal/container/Warning";
import { useModal } from "../../../Modal/Modal";
import { ActionButtonWrapper } from "./ActionButton";
import WebhookTestPopover from "./WebhookTestPopover";

interface ContainerProps {
  selected: boolean;
  error: boolean;
  isFormOpen?: boolean;
}

const Container = styled(motion.div)<ContainerProps>`
  width: ${(props) => (props.isFormOpen ? "82px" : "480px")};
  background: #fff;
  height: fit-content;
  padding: 16px;
  border-radius: 4px;
  border: 1px solid #dddddd;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  cursor: pointer;
  position: relative;
  transition: box-shadow 0.3s, border 0.3s;

  ${(props) =>
    props.selected &&
    css`
      box-shadow: 0px 0px 6px #fb972e4d;
      border: 1px solid #fb972e;
    `}
  ${(props) =>
    props.error &&
    css`
      box-shadow: 0px 0px 6px #ff0000;
      border: 1px solid #ff0000;
    `}



    @media screen and (min-width: 1080px) {
    width: 480px;
    min-height: fit-content;
    justify-content: start;
  }
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  margin-left: 20px;
`;

const Title = styled.div`
  color: #2f2f2f;
  font-size: 18px;
  font-weight: 600;
`;

const Text = styled.div`
  color: #2f2f2f;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.4;
  display: inline-block;
  width: 370px;
  white-space: nowrap;
  overflow: hidden !important;
  text-overflow: ellipsis;
`;

const Flex = styled.div`
  display: flex;
  align-items: center;
`;

interface IconButtonProps {
  isDelete?: boolean;
  isFormOpen?: boolean;
}

const IconButton = styled.div<IconButtonProps>`
  height: 24px;
  width: 24px;
  top: 12px;
  position: absolute;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: "#ccc";
  transition: opacity 0.2s;
  pointer-events: ${(props) => (props.isDelete ? "default" : "none")};
  opacity: ${(props) => (props.isDelete ? 1 : 0)};

  @media screen and (max-width: 1079px) {
    height: ${(props) => (props.isFormOpen ? "20px" : "24px")};
    width: ${(props) => (props.isFormOpen ? "20px" : "24px")};
    top: ${(props) => (props.isFormOpen ? "2px" : "12px")};
  }
`;

const CloseButton = styled(IconButton)<IconButtonProps>`
  right: 6px;

  @media screen and (max-width: 1079px) {
    right: ${(props) => (props.isFormOpen ? "4px" : "6px")};
  }
`;

const CloneButton = styled(IconButton)<IconButtonProps>`
  right: 35px;

  @media screen and (max-width: 1079px) {
    right: ${(props) => (props.isFormOpen ? "33px" : "35px")};
  }
`;

const TestButton = styled(IconButton)<IconButtonProps>`
  right: 64px;
  top: 15px;

  @media screen and (max-width: 1079px) {
    right: ${(props) => (props.isFormOpen ? "58px" : "60px")};
    top: 5px;
  }
`;

const ClonePopover = styled.div`
  position: absolute;
  bottom: -62px;
  left: -1px;
  height: 64px;
  background-color: white;
  color: #2f2f2f;
  width: 160px;
  text-align: left;
  border-radius: 0px 5px 5px 5px;
  border: 1px solid #ddd;
`;

const TestPopover = styled.div`
  position: absolute;
  bottom: -62px;
  left: -1px;
  height: 100px;
  background-color: white;
  color: #2f2f2f;
  width: 200px;
  text-align: left;
  border-radius: 0px 5px 5px 5px;
  border: 1px solid #ddd;
`;

const ClonePopoverItem = styled.div`
  padding: 5px;
  height: 31px;
  cursor: pointer;
  color: #2f2f2f;
  :hover {
    background-color: #f1f1f1;
  }
`;

interface ActionButtonCardProps {
  onClick?: () => void;
  onDelete?: (e: any) => void;
  type: INodeTypes;
  description: string | JSX.Element;
  selected: boolean;
  error: boolean;
  original: boolean;
  noDelete?: boolean;
  clone: (type: "bottom" | "next") => void;
  shouldCloneToEnd: boolean;
  content: INode;
}

export default function NodeCard({
  onClick,
  onDelete,
  type,
  original,
  description,
  selected,
  error,
  noDelete,
  clone,
  shouldCloneToEnd,
  content,
}: ActionButtonCardProps) {
  const [form, setForm] = useRecoilState(CampaingBuilderFormAtom);
  const action = useRecoilValue(CampaignActionAtom);
  const [isHover, setIsHover] = useState<boolean>(false);
  const modal = useModal();
  const isLargerThan1080 = useMediaQuery("(min-width: 1080px)");
  const [showClonePopover, setShowClonePopover] = useState<boolean>(false);
  const [showTestPopover, setShowTestPopover] = useState<boolean>(false);

  function handleCloneClick(e: MouseEvent, type: "bottom" | "next") {
    e.stopPropagation();
    clone(type);
  }

  const value = (() => {
    switch (type) {
      case INodeTypes.ManageTag:
        return {
          icon: <FaTag />,
          title: "Apply or remove tag(s)",
          backgroundColor: "#97D49A",
        };
      case INodeTypes.Email:
        return {
          icon: <FaEnvelope />,
          title: "Send an email",
          backgroundColor: "#68C2D3",
        };
      case INodeTypes.WebHook:
        return {
          icon: (
            <img
              src={"./icons/webhook-icon.svg"}
              css={css`
                margin-top: 10px;
              `}
            />
          ),
          title: "Trigger a webhook",
          backgroundColor: "#997CF8",
        };
      case INodeTypes.PushNotification:
        return {
          icon: <FaBell />,
          title: "Turn on notifications",
          backgroundColor: "#E77480",
        };
      case INodeTypes.StopTag:
        return {
          icon: <FaBell />,
          title: "Stop the campaign",
          backgroundColor: "#5A5353",
        };
      case INodeTypes.Sms:
        return {
          icon: <BsFillChatFill />,
          title: "Send an SMS notification",
          backgroundColor: "#D9AA53",
        };
    }
  })();

  return (
    <Container
      isFormOpen={form !== null}
      layout
      onClick={onClick}
      selected={selected}
      error={error}
      onHoverStart={() => setIsHover(true)}
      onHoverEnd={() => setIsHover(false)}
    >
      {type === INodeTypes.WebHook && (
        <TestButton
          isFormOpen={form !== null}
          isDelete={isHover}
          onMouseEnter={() => setShowTestPopover(true)}
          onMouseLeave={() => setShowTestPopover(false)}
          onClick={(e: any) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <GrTest
            size={17}
            css={css`
              * {
                stroke: #bbb;
              }
            `}
          />
          {showTestPopover && <WebhookTestPopover webhook={content} />}
        </TestButton>
      )}
      {!noDelete && (
        <CloseButton
          isFormOpen={form !== null}
          isDelete={isHover}
          onClick={(node: any) => {
            if (action === "create" && original) {
              modal.pushModal(
                <Warning
                  buttonsName={["No", "Yes"]}
                  title="Delete campaign node?"
                  description="Are you sure you want to permanently delete this campaign node?"
                  icon={"./icons/delete.svg"}
                  onOkClicked={() => {
                    onDelete(node);
                    modal.popModal();
                  }}
                  onCancelClicked={() => modal.popModal()}
                />
              );
            } else {
              onDelete(node);
            }
          }}
        >
          <IoMdClose size={24} color={"#ccc"} />
        </CloseButton>
      )}
      {!noDelete && (
        <CloneButton
          isFormOpen={form !== null}
          isDelete={isHover}
          onMouseEnter={
            !shouldCloneToEnd ? () => setShowClonePopover(true) : null
          }
          onMouseLeave={
            !shouldCloneToEnd ? () => setShowClonePopover(false) : null
          }
          onClick={
            shouldCloneToEnd
              ? (e) => {
                  handleCloneClick(e as any, "bottom");
                }
              : null
          }
        >
          <HiOutlineDuplicate size={24} color={"#ccc"} />
          {showClonePopover && (
            <ClonePopover>
              <ClonePopoverItem
                onClick={(e) => handleCloneClick(e as any, "next")}
              >
                Clone below
              </ClonePopoverItem>
              <ClonePopoverItem
                onClick={(e) => handleCloneClick(e as any, "bottom")}
              >
                Clone to bottom
              </ClonePopoverItem>
            </ClonePopover>
          )}
        </CloneButton>
      )}
      <Flex>
        <ActionButtonWrapper
          css={css`
            box-shadow: none;
          `}
          backgroundColor={value?.backgroundColor || ""}
          justIcon
        >
          {value?.icon}
        </ActionButtonWrapper>

        {(isLargerThan1080 || form === null) && (
          <TextWrapper>
            <Flex
              style={{
                justifyContent: "space-between",
                width: "calc(100% - 15px)",
              }}
            >
              <Title>{value?.title}</Title>
            </Flex>
            <Text>{description}</Text>
          </TextWrapper>
        )}
      </Flex>
    </Container>
  );
}
